import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  useTheme,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GetUser, UserUpdatePhone, UserUpdateVerification } from '../../services/profile';

const EditPhone = () => {
  const [phone, setPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('90');
  const [hasCode, setHasCode] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await GetUser();
      if (response.statusCode === 200) {
        setPhone(response.data.phone);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t('editPhone.error'));
    }
  };

  const validatePhone = () => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone);
  };

  const handleUpdatePhone = async () => {
    if (!validatePhone()) {
      toast.error(t('editPhone.invalidPhone'));
      return;
    }

    setIsLoading(true);
    try {
      const formattedPhone = phone.startsWith('+') ? phone.slice(1) : phone;
      const response = await UserUpdatePhone(formattedPhone, phoneCode);
      
      if (response.statusCode === 200) {
        toast.success(response.message);
        setHasCode(true);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t('editPhone.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerification = async () => {
    if (!activationCode || activationCode.length !== 6) {
      toast.error(t('editPhone.invalidCode'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await UserUpdateVerification(activationCode);
      if (response.statusCode === 200) {
        toast.success(response.message);
        navigate(-1);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t('editPhone.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        px: 2,
        background: theme.palette.background.default
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={theme.palette.mode === 'dark' ? 3 : 1}
          sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
        >
          <Typography variant="h5" component="h1" align="center" gutterBottom>
            {t('editPhone.title')}
          </Typography>

          <Box sx={{ mt: 3 }}>
            {!hasCode ? (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel>{t('editPhone.countryCode')}</InputLabel>
                  <Select
                    value={phoneCode}
                    label={t('editPhone.countryCode')}
                    onChange={(e) => setPhoneCode(e.target.value)}
                  >
                    <MenuItem value="90">+90 (TR)</MenuItem>
                    <MenuItem value="1">+1 (US/CA)</MenuItem>
                    <MenuItem value="44">+44 (UK)</MenuItem>
                    <MenuItem value="49">+49 (DE)</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label={t('editPhone.phoneLabel')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  placeholder={t('editPhone.phonePlaceholder')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">+{phoneCode}</InputAdornment>
                    ),
                  }}
                />
              </>
            ) : (
              <>
                <Typography align="center" sx={{ mb: 2 }}>
                  {t('editPhone.codeInstruction')}
                </Typography>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label={t('editPhone.codeLabel')}
                  value={activationCode}
                  onChange={(e) => setActivationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{
                    maxLength: 6,
                    pattern: '[0-9]*'
                  }}
                  placeholder={t('editPhone.codePlaceholder')}
                />
              </>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => hasCode ? setHasCode(false) : navigate(-1)}
                disabled={isLoading}
              >
                {t('common.back')}
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={hasCode ? handleVerification : handleUpdatePhone}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  t('common.submit')
                )}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default EditPhone; 